import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { usePriceMatchStore } from '../../../store/priceMatchStore';
import { PriceMatchRequest } from '../../../services/pricingApi';
import { listSkus, Sku } from '../../../services/skusApi';
import { customersApi, Customer } from '../../../services/customersApi';
import styles from './SalesRepPriceMatchRequests.module.css';

function statusClass(status: PriceMatchRequest['approval_status']): string {
  switch (status) {
    case 'APPROVED': return styles.statusApproved;
    case 'DENIED': return styles.statusDenied;
    case 'EXPIRED': return styles.statusExpired;
    default: return styles.statusPending;
  }
}

const SalesRepPriceMatchRequests: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const items = usePriceMatchStore((s) => s.items);
  const loading = usePriceMatchStore((s) => s.loading);
  const saving = usePriceMatchStore((s) => s.saving);
  const error = usePriceMatchStore((s) => s.error);
  const fetchList = usePriceMatchStore((s) => s.fetchList);
  const create = usePriceMatchStore((s) => s.create);
  const approve = usePriceMatchStore((s) => s.approve);

  const [skus, setSkus] = useState<Sku[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [skuId, setSkuId] = useState<number | ''>('');
  const [customerId, setCustomerId] = useState<number | ''>('');
  const [requestedPrice, setRequestedPrice] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    fetchList();
    listSkus({ limit: 200 }).then((r) => setSkus(r.data ?? [])).catch(() => setSkus([]));
    customersApi.list({ limit: 200 }).then((r: any) => {
      const data = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : [];
      setCustomers(data);
    }).catch(() => setCustomers([]));
  }, [fetchList]);

  const skuById = useMemo(() => new Map(skus.map((s) => [s.id, s])), [skus]);
  const customerById = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers]);

  const visibleItems = useMemo(() => {
    if (isAdmin) return items;
    // Backend doesn't scope GET by requester — filter to the rep's own
    // requests client-side (requester_id is this rep's own users.id).
    return items.filter((r) => String(r.requester_id) === String(user?.id));
  }, [items, isAdmin, user?.id]);

  const selectedSku = skuId === '' ? null : skuById.get(skuId) ?? null;

  const handleCreate = async () => {
    setFormError(null);
    if (skuId === '') { setFormError('Select a product'); return; }
    const price = parseFloat(requestedPrice);
    if (!Number.isFinite(price) || price <= 0) { setFormError('Enter a valid requested price'); return; }
    try {
      await create({
        sku_id: skuId,
        customer_id: customerId === '' ? undefined : customerId,
        requested_price: price,
        competitor_documentation_url: competitorUrl.trim() || undefined,
        cost_of_goods: selectedSku?.unit_cost,
        reason: reason.trim() || undefined,
      });
      setShowForm(false);
      setSkuId(''); setCustomerId(''); setRequestedPrice(''); setCompetitorUrl(''); setReason('');
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create request');
    }
  };

  const handleApprove = async (id: number) => {
    try { await approve(id, { approval_status: 'APPROVED' }); }
    catch (err) { window.alert(err instanceof Error ? err.message : 'Failed to approve'); }
  };

  const handleDeny = async (id: number) => {
    const denial_reason = window.prompt('Reason for denial:') || undefined;
    try { await approve(id, { approval_status: 'DENIED', denial_reason }); }
    catch (err) { window.alert(err instanceof Error ? err.message : 'Failed to deny'); }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Price Match Requests</h2>
          <p className={styles.subtitle}>
            {isAdmin
              ? 'Review and act on price match requests from the field.'
              : 'Request a price match for a customer and track its approval status.'}
          </p>
        </div>
        {!isAdmin && (
          <button type="button" className={styles.newBtn} onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Cancel' : '+ New Request'}
          </button>
        )}
      </div>

      {showForm && !isAdmin && (
        <div className={styles.formCard}>
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.label}>Product</label>
              <select className={styles.select} value={skuId} onChange={(e) => setSkuId(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">Select a product</option>
                {skus.map((s) => (
                  <option key={s.id} value={s.id}>{s.product_name} ({s.sku_code})</option>
                ))}
              </select>
              {selectedSku && (
                <p className={styles.hint}>List price: ${Number(selectedSku.list_price ?? 0).toFixed(2)}</p>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Customer</label>
              <select className={styles.select} value={customerId} onChange={(e) => setCustomerId(e.target.value === '' ? '' : Number(e.target.value))}>
                <option value="">Select a customer</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.store_name || c.full_name || c.email}</option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Requested Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={styles.input}
                value={requestedPrice}
                onChange={(e) => setRequestedPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Competitor Documentation URL (optional)</label>
              <input
                type="text"
                className={styles.input}
                value={competitorUrl}
                onChange={(e) => setCompetitorUrl(e.target.value)}
                placeholder="https://…"
              />
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Reason (optional)</label>
            <textarea
              className={styles.textarea}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why should this price be matched?"
            />
          </div>
          {formError && <p className={styles.errorText}>{formError}</p>}
          <button type="button" className={styles.submitBtn} onClick={handleCreate} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      )}

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && visibleItems.length === 0}
        emptyMessage={isAdmin ? 'No price match requests yet' : "You haven't requested a price match yet"}
        onRetry={() => fetchList()}
      >
        <div className={styles.list}>
          {visibleItems.map((r) => {
            const sku = skuById.get(r.sku_id);
            const customer = r.customer_id != null ? customerById.get(r.customer_id) : undefined;
            return (
              <div key={r.id} className={styles.row}>
                <div className={styles.rowMain}>
                  <div className={styles.rowHeader}>
                    <span className={styles.productName}>{sku?.product_name || `SKU #${r.sku_id}`}</span>
                    <span className={`${styles.statusBadge} ${statusClass(r.approval_status)}`}>{r.approval_status}</span>
                  </div>
                  <p className={styles.rowMeta}>
                    Requested ${Number(r.requested_price).toFixed(2)}
                    {sku?.list_price != null && ` · list $${Number(sku.list_price).toFixed(2)}`}
                    {customer && ` · for ${customer.store_name || customer.full_name}`}
                  </p>
                  {r.denial_reason && <p className={styles.denialReason}>Denied: {r.denial_reason}</p>}
                  {r.approval_status === 'APPROVED' && r.expiration_date && (
                    <p className={styles.rowMeta}>Expires {new Date(r.expiration_date).toLocaleDateString()}</p>
                  )}
                  <p className={styles.timestamp}>Submitted {new Date(r.created_at).toLocaleString()}</p>
                </div>
                {isAdmin && r.approval_status === 'PENDING' && (
                  <div className={styles.actions}>
                    <button type="button" className={styles.approveBtn} onClick={() => handleApprove(r.id)} disabled={saving}>
                      Approve
                    </button>
                    <button type="button" className={styles.denyBtn} onClick={() => handleDeny(r.id)} disabled={saving}>
                      Deny
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepPriceMatchRequestsPage: React.FC = () => <SalesRepPriceMatchRequests />;

export { SalesRepPriceMatchRequests };
export default SalesRepPriceMatchRequests;
